import React from 'react';
import { Phone } from 'lucide-react';
import { formatPhone } from './powerDialConstants.js';

export default function SmartCallerIdSection({
  smartLocalId,
  setSmartLocalId,
  callerIdData,
  selectedCallerId,
  setSelectedCallerId,
  matchPreview,
}) {
  return (
    <div className="mb-4 p-3 rounded-xl border border-white/10 bg-white/3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white flex items-center gap-1.5">
            <span>Smart Local ID</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-normal">
              {smartLocalId ? 'Dynamic Match' : 'Fixed Number'}
            </span>
          </div>
          <div className="text-[11px] text-white/50 mt-0.5 leading-snug">
            {smartLocalId
              ? "Matches outbound caller ID to prospect's area code or state location."
              : "Uses a fixed selected caller ID for all outbound dials."}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={smartLocalId}
          onClick={() => setSmartLocalId(!smartLocalId)}
          title="Toggle Smart Local ID dynamic matching"
          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors shrink-0 ${
            smartLocalId ? 'bg-civix-purple' : 'bg-white/20'
          }`}
        >
          <div
            className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
              smartLocalId ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Dynamic Match Preview or Fixed Selector */}
      {smartLocalId ? (
        <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-white/70">
              Dynamic Matching Strategy:
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold border border-emerald-500/30">
              Area Code &bull; In-State &bull; Neighbor State
            </span>
          </div>

          {matchPreview?.leadCount > 0 && (
            <div className="p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-white/70 leading-snug space-y-1.5">
              <div>
                <span className="font-semibold text-white">{matchPreview.leadCount} {matchPreview.leadCount === 1 ? 'lead' : 'leads'}</span>{' '}
                selected across <span className="font-semibold text-white">{matchPreview.stateCount} {matchPreview.stateCount === 1 ? 'state' : 'states'}</span>
                {' '}({Object.entries(matchPreview.states || {}).map(([s, n]) => `${s} ×${n}`).join(', ')}).
                {' '}Smart Local ID will match exact area codes, in-state numbers, or nearest neighboring state lines dynamically for each call.
              </div>
              <div className="flex flex-wrap gap-1">
                {[['local', '🎯 Local', 'bg-emerald-500/20 text-emerald-300'],
                  ['state', '📍 In-State', 'bg-cyan-500/20 text-cyan-300'],
                  ['neighbor', '🗺️ Neighbor', 'bg-purple-500/20 text-purple-300'],
                  ['default', '📞 Default', 'bg-white/10 text-white/70']].map(([k, label, cls]) => (
                  matchPreview.tiers?.[k] > 0 && (
                    <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>
                      {label} ×{matchPreview.tiers[k]}
                    </span>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Box showing the automatically selected number from bought numbers */}
          <div className="p-2.5 rounded-lg bg-black/40 border border-white/10 space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-white/60">
                Automatically Selected Fallback Number:
              </span>
              <span className="text-[10px] text-civix-purpleSoft font-medium">
                (From Bought Numbers)
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <div className="font-mono text-xs font-bold text-white flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-emerald-400" />
                {formatPhone(selectedCallerId || callerIdData.defaultCallerId || '+16615249257')}
              </div>

              {callerIdData.numbers && callerIdData.numbers.length > 1 ? (
                <select
                  value={selectedCallerId || callerIdData.defaultCallerId || ''}
                  onChange={(e) => setSelectedCallerId(e.target.value)}
                  className="bg-[#12111A] border border-white/15 rounded px-2 py-1 text-[11px] text-white/90 focus:border-civix-purple"
                  title="Select which bought phone number is used as the default fallback"
                >
                  {callerIdData.numbers.map((n) => (
                    <option key={n.number} value={n.number}>
                      {formatPhone(n.number)} {n.state ? `(${n.state})` : ''} {n.isAssignedToMe ? '★ Assigned' : ''}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>

            <div className="text-[10px] text-white/45 leading-tight pt-1">
              If a prospect has no local area code or state match in your pool of {callerIdData.totalOwned || callerIdData.numbers?.length || 1} bought numbers,{' '}
              <span className="text-white/80 font-mono font-medium">{formatPhone(selectedCallerId || callerIdData.defaultCallerId || '+16615249257')}</span> will automatically be used.
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 pt-2.5 border-t border-white/10">
          <label className="block text-[11px] text-white/60 mb-1 font-medium">
            Selected Outbound Caller ID:
          </label>
          {callerIdData.numbers && callerIdData.numbers.length > 0 ? (
            <select
              value={selectedCallerId}
              onChange={(e) => setSelectedCallerId(e.target.value)}
              className="w-full bg-[#12111A] border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-hidden focus:border-civix-purple"
            >
              {callerIdData.numbers.map((n) => (
                <option key={n.number} value={n.number}>
                  {formatPhone(n.number)} {n.state ? `(${n.state})` : ''} {n.isAssignedToMe ? '— My Assigned Line' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-xs text-white/80 font-mono bg-white/5 p-2 rounded border border-white/10">
              {formatPhone(callerIdData.defaultCallerId || '+16615249257')} (Default Outbound Line)
            </div>
          )}
        </div>
      )}
    </div>
  );
}
